import { useQuery } from "@apollo/client";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useOmnisearchState } from "hooks/omnisearch-v2";
import { GET_STAFF_CERS_REJECTION_FINDINGS } from "hooks/useStaffCersRejectionFindings";
import { ReactNode } from "react";
import { KeyMetric } from "routes/Staff/Fulfillment/WorkflowOverviewStats/KeyMetric";

const SECTION_LABEL_SX = {
  mb: 1.5,
  display: "block",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "0.7rem",
} as const;

/** One titled cluster of KeyMetric cards (local copy of the gen-status layout). */
function StatSection({
  label,
  color,
  children,
}: {
  label: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color={color} sx={SECTION_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
        {children}
      </Box>
    </Box>
  );
}

function SectionDivider() {
  return <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "flex" } }} />;
}

/**
 * Dashboard cards above the rejections grid. Reads stats from a lightweight
 * companion query (same operation, pageSize 1) scoped to the same omnisearch, so
 * counts always match the filtered grid. Each card links with keysToKeep: [] —
 * clicking resets to just that bucket so the grid matches the card's count.
 */
export function RejectionStatCards() {
  const { omnisearch, createUrlFromOmnisearchKeyValues } = useOmnisearchState();

  const { data, loading } = useQuery(GET_STAFF_CERS_REJECTION_FINDINGS, {
    variables: { search: omnisearch, page: 0, pageSize: 1 },
    fetchPolicy: "cache-and-network",
  });
  const stats = data?.staffCersRejectionFindings.stats;

  // Clicking a card resets the bucket filters (status/module/CUPA/…) so you land on
  // exactly the count shown — but preserve `csm`, so a CSM with "My Findings" on stays
  // in their personal scope (their card counts are already csm-scoped; wiping csm here
  // would drop them to the global set and break count-integrity). No-op when off.
  const cardHref = (key: string, value: string) =>
    createUrlFromOmnisearchKeyValues({ keyValues: [[key, value]], keysToKeep: ["csm"] });

  return (
    <Paper elevation={0} sx={{ px: 3, py: 2.5 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={3} sx={{ minWidth: 0 }}>
        <StatSection label="Findings" color="warning.main">
          <KeyMetric
            name="Unresolved"
            value={stats?.unresolved}
            loading={loading}
            href={cardHref("status", "UNRESOLVED")}
          />
          <KeyMetric
            name="Out of Compliance >30d"
            value={stats?.oocOver30}
            loading={loading}
            href={cardHref("oocOver30", "true")}
          />
          <KeyMetric
            name="Pending"
            value={stats?.pending}
            loading={loading}
            href={cardHref("status", "PENDING")}
          />
          <KeyMetric
            name="Resolved This Month"
            value={stats?.resolvedThisMonth}
            loading={loading}
            href={cardHref("resolvedThisMonth", "true")}
          />
        </StatSection>

        <SectionDivider />

        <StatSection label="Aging" color="error.main">
          <KeyMetric
            name="30–60 Days"
            value={stats?.age30to60}
            loading={loading}
            href={cardHref("ageBucket", "30_60")}
          />
          <KeyMetric
            name="60–90 Days"
            value={stats?.age60to90}
            loading={loading}
            href={cardHref("ageBucket", "60_90")}
          />
          <KeyMetric
            name="90+ Days"
            value={stats?.age90plus}
            loading={loading}
            href={cardHref("ageBucket", "90_plus")}
          />
        </StatSection>
      </Stack>
    </Paper>
  );
}
